import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppTips - A TOOLTIP THAT A FINGER AND THE KEYBOARD CAN REACH (audit 2026-10-01).
//
// ⭐ WHY: the owner ruled that explanations are tooltips, not lines of the screen (2026-09-28) - and a `title` is shown
// by the browser ONLY to a mouse that rests on it. On a phone, and for anybody working the page with the keyboard, every
// one of those explanations did not exist: what the Send button's note says, what "both" means on a task, what an
// icon-only button does, why a count is what it is. The audit counted 7,679 such places over the signed-in screens.
//
// ⭐ WHAT IT DOES, for EVERY element with a `title`, with no change to any markup:
//   * the keyboard: a control that takes the focus by the keyboard (`:focus-visible`) shows its title after a short
//     pause (`FocusMs` - walking through with Tab does not flicker), until the focus leaves or a key is pressed;
//   * a finger, on a control (a button, a link, a field): PRESS AND HOLD (`HoldMs`) shows it, and the lift that ends
//     the hold does NOT press the control - reading what Send does must never send;
//   * a finger, on anything else (a heading, a chip, a count): a tap shows it.
//   The tip goes away on the next tap anywhere, on a scroll, after `StayMs`.
// The browser's own tooltip for a resting mouse is left exactly as it is.
//
// ⛔ IT SHOWS WORDS AND NOTHING ELSE: `textContent` (a title is the page's own text, and still never markup), no click
// is made, nothing is sent (decision 9) - a held press only ever CANCELS the click it would have been.
// ⛔ NO INLINE STYLE IN MARKUP (the CSP): the tip is an element made with `createElement`, placed through the CSSOM.
//
// `AppTipRules` is pure (the suites test it); `AppTips` is the browser half, one per document - the app's own and each
// contact frame's (`AppMain.Frame` installs it on the frame's `load`).
// A second consumer: any page whose explanations live in `title`. Nothing here names a product; the one project fact
// (`Skip` - elements that already answer a tap themselves) is passed in by the host.

/** Where a tip goes. */
export class AppTipPlace {
    public Left: number = 0;
    public Top: number = 0;
}

export class AppTipRules {
    /** A finger held this long on a control asks for its tip. */
    public static readonly HoldMs: number = 500;
    /** The keyboard's focus rests this long before its tip shows. */
    public static readonly FocusMs: number = 450;
    /** A finger that moved more than this was scrolling, not holding. */
    public static readonly MovePx: number = 10;
    /** A tip shown to a finger stays this long. */
    public static readonly StayMs: number = 6000;
    /** The click that ends a held press is swallowed for this long. */
    public static readonly SwallowMs: number = 700;
    public static readonly Gap: number = 8;
    public static readonly Margin: number = 8;
    public static readonly TipClass: string = "wac-tip";
    /** On `<html>` while a document's tips are installed - what the harness reads. */
    public static readonly MarkAttr: string = "data-wac-tips";

    /**
     * The words a tip says for an element: its title - unless the element already SHOWS exactly those words (a tip that
     * repeats the text under it is noise). "" = no tip.
     */
    public static WordsFor = (Title: string, Shown: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Words: string = AppTipRules.Tidy(Title);
            if (Words.length === 0) { return ""; }
            if (Words === AppTipRules.Tidy(Shown)) { return ""; }
            return Words;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTipRules.WordsFor");
            return "";
        }
    };

    /** One line, single spaces. */
    private static Tidy = (Text: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return nz(Text).split(/\s+/).join(" ").trim();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTipRules.Tidy");
            return "";
        }
    };

    /**
     * Does a tap on this element already DO something (so a finger must press and hold for the tip)? A link, a button,
     * a field, a summary, a label, or anything given a button's role.
     */
    public static IsControl = (Tag: string, Role: string, HasHref: boolean): boolean => {
        if (getCoreUtils().KErrorMode === true) { return true; }
        try {
            const Name: string = nz(Tag).toLowerCase();
            if (Name === "a") { return HasHref === true; }
            if (["button", "input", "select", "textarea", "summary", "label"].indexOf(Name) >= 0) { return true; }
            return ["button", "link", "menuitem", "menuitemradio", "tab", "option", "checkbox", "radio", "switch"].indexOf(nz(Role).toLowerCase()) >= 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTipRules.IsControl");
            return true;
        }
    };

    /**
     * Under what the element shows (`Bottom` = the lowest edge of it and of anything it draws), centred on it, kept
     * inside the window; above it when there is no room below.
     */
    public static Place = (Left: number, Top: number, Width: number, Bottom: number, TipWidth: number, TipHeight: number,
        ViewWidth: number, ViewHeight: number): AppTipPlace => {
        const Out: AppTipPlace = new AppTipPlace();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            const M: number = AppTipRules.Margin;
            let X: number = Left + Width / 2 - TipWidth / 2;
            if (X + TipWidth > ViewWidth - M) { X = ViewWidth - M - TipWidth; }
            if (X < M) { X = M; }
            let Y: number = Bottom + AppTipRules.Gap;
            if (Y + TipHeight > ViewHeight - M && Top - AppTipRules.Gap - TipHeight >= M) { Y = Top - AppTipRules.Gap - TipHeight; }
            Out.Left = Math.round(X);
            Out.Top = Math.round(Y);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTipRules.Place");
            return Out;
        }
    };

    /** Did the finger move far enough to be a scroll? */
    public static Moved = (X0: number, Y0: number, X1: number, Y1: number): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return Math.abs(X1 - X0) > AppTipRules.MovePx || Math.abs(Y1 - Y0) > AppTipRules.MovePx;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTipRules.Moved");
            return false;
        }
    };

    /** The stylesheet for a document's tip - the app's is in app.css; the contact frame gets this one. Tokens only. */
    public static Css = (): string => {
        // DOCUMENTED EXCEPTION: a constant. ERROR-SUPPRESSED-JUSTIFIED: returns a literal.
        return ".wac-tip{position:fixed;z-index:60;max-width:min(320px,calc(100vw - 16px));padding:6px 10px;border-radius:8px;"
            + "background:var(--text);color:var(--surface);font:12px/1.4 -apple-system,BlinkMacSystemFont,\"Segoe UI\",Roboto,Helvetica,Arial,sans-serif;"
            + "box-shadow:0 4px 16px rgba(16,20,28,.25);pointer-events:none;white-space:normal;overflow-wrap:anywhere}"
            + ".wac-tip[hidden]{display:none}";
    };
}

/** One document's tips. */
export class AppTips {
    /** Elements that already answer a tap with their own words (a selector; set by the host before `Install`). */
    public static Skip: string = "";

    private static Made: WeakMap<Document, AppTips> = new WeakMap<Document, AppTips>();

    private Doc: Document;
    private Tip: HTMLElement | null = null;
    private For: Element | null = null;
    private HoldTimer: number = 0;
    private FocusTimer: number = 0;
    private StayTimer: number = 0;
    private DownX: number = 0;
    private DownY: number = 0;
    private Held: Element | null = null;
    private SwallowUntil: number = 0;

    private constructor(Doc: Document) {
        this.Doc = Doc;
    }

    /** Install on a document, once (a second call for the same document does nothing). */
    public static Install = (Doc: Document | null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Doc === null || AppTips.Made.has(Doc) === true) { return; }
            const Run: AppTips = new AppTips(Doc);
            AppTips.Made.set(Doc, Run);
            Doc.addEventListener("focusin", Run.OnFocusIn, true);
            Doc.addEventListener("focusout", Run.OnFocusOut, true);
            Doc.addEventListener("pointerdown", Run.OnDown, true);
            Doc.addEventListener("pointermove", Run.OnMove, true);
            Doc.addEventListener("pointerup", Run.OnUp, true);
            Doc.addEventListener("pointercancel", Run.OnCancel, true);
            Doc.addEventListener("click", Run.OnClick, true);
            Doc.addEventListener("keydown", Run.OnKey, true);
            Doc.addEventListener("scroll", Run.OnScroll, true);
            Doc.documentElement.setAttribute(AppTipRules.MarkAttr, "1");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.Install");
        }
    };

    /** The nearest element with a title that this document may show a tip for, or null. */
    private Carrier = (Target: EventTarget | null): Element | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Node: Element | null = Target as Element | null;
            if (Node === null || typeof Node.closest !== "function") { return null; }
            const El: Element | null = Node.closest("[title]");
            if (El === null) { return null; }
            if (AppTips.Skip.length > 0 && El.matches(AppTips.Skip) === true) { return null; }
            if (AppTipRules.WordsFor(nz(El.getAttribute("title")), nz((El as HTMLElement).innerText)).length === 0) { return null; }
            return El;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.Carrier");
            return null;
        }
    };

    /** Is `El` - or something it sits in - a control (a tap on it already does something)? */
    private static Presses = (El: Element): boolean => {
        if (getCoreUtils().KErrorMode === true) { return true; }
        try {
            for (let At: Element | null = El; At !== null; At = At.parentElement) {
                if (AppTipRules.IsControl(At.tagName, nz(At.getAttribute("role")), At.hasAttribute("href")) === true) { return true; }
            }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.Presses");
            return true;
        }
    };

    /** A timer in this document's own window (the frame's, for a frame). 0 = the document has no window any more. */
    private Later = (Fn: () => void, Ms: number): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            const View: Window | null = this.Doc.defaultView;
            if (View === null) { return 0; }
            return View.setTimeout(Fn, Ms);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.Later");
            return 0;
        }
    };

    private Drop = (Timer: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const View: Window | null = this.Doc.defaultView;
            if (View !== null) { View.clearTimeout(Timer); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.Drop");
        }
    };

    // ── The keyboard ─────────────────────────────────────────────────────────────────────────

    private OnFocusIn = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.ClearFocusTimer();
            const El: Element | null = this.Carrier(Ev.target);
            // ⚠ Only a focus the KEYBOARD brought (`:focus-visible`): a clicked button must not sprout a tip.
            if (El === null || El !== Ev.target || El.matches(":focus-visible") === false) { return; }
            this.Held = El;
            this.FocusTimer = this.Later(this.OnFocusRest, AppTipRules.FocusMs);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.OnFocusIn");
        }
    };

    private OnFocusRest = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.FocusTimer = 0;
            if (this.Held === null || this.Doc.activeElement !== this.Held) { return; }
            this.Show(this.Held, false);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.OnFocusRest");
        }
    };

    private OnFocusOut = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.ClearFocusTimer();
            this.Hide();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.OnFocusOut");
        }
    };

    private OnKey = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.ClearFocusTimer();
            this.Hide();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.OnKey");
        }
    };

    private ClearFocusTimer = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.FocusTimer !== 0) { this.Drop(this.FocusTimer); this.FocusTimer = 0; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.ClearFocusTimer");
        }
    };

    // ── A finger ─────────────────────────────────────────────────────────────────────────────

    private OnDown = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const P: PointerEvent = Ev as PointerEvent;
            this.Hide();
            this.ClearHold();
            if (P.pointerType !== "touch" && P.pointerType !== "pen") { return; }
            const El: Element | null = this.Carrier(Ev.target);
            if (El === null || AppTips.Presses(El) === false) { return; }
            this.Held = El;
            this.DownX = P.clientX;
            this.DownY = P.clientY;
            this.HoldTimer = this.Later(this.OnHeld, AppTipRules.HoldMs);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.OnDown");
        }
    };

    private OnHeld = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.HoldTimer = 0;
            if (this.Held === null) { return; }
            // ⛔ THE LIFT THAT ENDS THIS HOLD MUST NOT PRESS THE CONTROL.
            this.SwallowUntil = Date.now() + AppTipRules.HoldMs * 20;
            this.Show(this.Held, true);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.OnHeld");
        }
    };

    private OnMove = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.HoldTimer === 0) { return; }
            const P: PointerEvent = Ev as PointerEvent;
            if (AppTipRules.Moved(this.DownX, this.DownY, P.clientX, P.clientY) === true) { this.ClearHold(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.OnMove");
        }
    };

    private OnUp = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.ClearHold();
            // The hold showed the tip: the click this lift makes is swallowed, but only for a moment.
            if (this.SwallowUntil > 0) { this.SwallowUntil = Date.now() + AppTipRules.SwallowMs; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.OnUp");
        }
    };

    private OnCancel = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.ClearHold();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.OnCancel");
        }
    };

    private ClearHold = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.HoldTimer !== 0) { this.Drop(this.HoldTimer); this.HoldTimer = 0; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.ClearHold");
        }
    };

    /**
     * A click. After a held press: swallowed (the control is not pressed). From a finger on something that is NOT a
     * control: its tip. Everything else is left to the page.
     */
    private OnClick = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.SwallowUntil > 0) {
                const Still: boolean = Date.now() <= this.SwallowUntil;
                this.SwallowUntil = 0;
                if (Still === true) { Ev.preventDefault(); Ev.stopPropagation(); return; }
            }
            const P: PointerEvent = Ev as PointerEvent;
            if (P.pointerType !== "touch" && P.pointerType !== "pen") { return; }
            const El: Element | null = this.Carrier(Ev.target);
            if (El === null || AppTips.Presses(El) === true) { return; }
            this.Show(El, true);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.OnClick");
        }
    };

    private OnScroll = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.ClearHold();
            this.Hide();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.OnScroll");
        }
    };

    // ── The tip ──────────────────────────────────────────────────────────────────────────────

    /** Show `El`'s title under it. `Timed` = it takes itself away (a finger has no "leave"). */
    private Show = (El: Element, Timed: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Words: string = AppTipRules.WordsFor(nz(El.getAttribute("title")), nz((El as HTMLElement).innerText));
            const View: Window | null = this.Doc.defaultView;
            if (Words.length === 0 || View === null || this.Doc.body === null) { return; }
            const Tip: HTMLElement = this.Element();
            // ⭐ INSIDE AN OPEN MODAL <dialog> THE TIP GOES INTO THE DIALOG (2026-10-13): a modal is drawn in the top
            // layer, above everything in <body> - a tip left in <body> was shown UNDER "Chat settings", i.e. not at all.
            let Home: Element = this.Doc.body;
            let Layer: Element | null = null;
            if (typeof El.closest === "function") { Layer = El.closest("dialog[open]"); }
            if (Layer !== null) { Home = Layer; }
            if (Tip.parentNode !== Home) { Home.appendChild(Tip); }
            Tip.textContent = Words;
            Tip.hidden = false;
            this.For = El;
            const Box: DOMRect = El.getBoundingClientRect();
            const Size: DOMRect = Tip.getBoundingClientRect();
            const At: AppTipPlace = AppTipRules.Place(Box.left, Box.top, Box.width, AppTips.LowestOf(El, Box.bottom), Size.width, Size.height,
                this.Doc.documentElement.clientWidth, View.innerHeight);
            // ⚠ Through the CSSOM (the policy governs `style=` in markup, never a script's own property write).
            Tip.style.left = String(At.Left) + "px";
            Tip.style.top = String(At.Top) + "px";
            if (this.StayTimer !== 0) { View.clearTimeout(this.StayTimer); this.StayTimer = 0; }
            if (Timed === true) { this.StayTimer = View.setTimeout(this.Hide, AppTipRules.StayMs); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.Show");
        }
    };

    /** The lowest edge of what `El` draws (a line that shows its own words in a bubble under itself on focus). */
    private static LowestOf = (El: Element, Bottom: number): number => {
        if (getCoreUtils().KErrorMode === true) { return Bottom; }
        try {
            let Low: number = Bottom;
            const Kids: NodeListOf<Element> = El.querySelectorAll("*");
            for (let At: number = 0; At < Kids.length && At < 24; At = At + 1) {
                const Box: DOMRect = Kids[At].getBoundingClientRect();
                if (Box.width > 2 && Box.height > 2 && Box.bottom > Low) { Low = Box.bottom; }
            }
            return Low;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.LowestOf");
            return Bottom;
        }
    };

    private Element = (): HTMLElement => {
        if (getCoreUtils().KErrorMode === true) { return this.Doc.createElement("div"); }
        try {
            if (this.Tip !== null && this.Tip.isConnected === true) { return this.Tip; }
            const Tip: HTMLElement = this.Doc.createElement("div");
            Tip.className = AppTipRules.TipClass;
            Tip.setAttribute("role", "tooltip");
            Tip.hidden = true;
            this.Doc.body.appendChild(Tip);
            this.Tip = Tip;
            return Tip;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.Element");
            return this.Doc.createElement("div");
        }
    };

    private Hide = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.StayTimer !== 0 && this.Doc.defaultView !== null) { this.Doc.defaultView.clearTimeout(this.StayTimer); this.StayTimer = 0; }
            this.For = null;
            if (this.Tip !== null && this.Tip.hidden === false) { this.Tip.hidden = true; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTips.Hide");
        }
    };
}
